import React, { useState, useEffect } from 'react';
import { Shield, Calendar, CheckCircle, XCircle, AlertTriangle, Building, Package, MapPin, Tag, User, Clock, FileText, Download, X } from 'lucide-react';
import axios from 'axios';

const EquipmentCheckModal = ({ equipmentId, isOpen, onClose }) => {
  const [equipment, setEquipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('info');

  const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

  useEffect(() => {
    if (isOpen && equipmentId) {
      fetchEquipment();
    }
  }, [equipmentId, isOpen]);

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      setError(null);
      setActiveTab('info'); // Reset to first tab
      
      // Public endpoint - no authentication needed
      const response = await axios.get(`${API_BASE_URL}/checkequipment/${equipmentId}/`);
      const equipmentData = response.data;
      
      // Try to fetch inspections data like ventroppe app does
      try {
        // Try multiple endpoints like ventroppe app
        const inspectionEndpoints = [
          `${API_BASE_URL}/inspection/?safety_equipment=${equipmentId}`,
          `${API_BASE_URL}/inspections/?safety_equipment=${equipmentId}`,
          `${API_BASE_URL}/safetyequipmentinspection/?safety_equipment=${equipmentId}`,
          `${API_BASE_URL}/equipmentinspection/?equipment_id=${equipmentId}`
        ];
        
        let inspectionsData = [];
        for (const endpoint of inspectionEndpoints) {
          try {
            const inspectionResponse = await axios.get(endpoint);
            const data = inspectionResponse.data;
            
            // Handle both direct array response and paginated response
            if (Array.isArray(data)) {
              inspectionsData = data;
            } else if (data && data.results && Array.isArray(data.results)) {
              inspectionsData = data.results;
            }
            
            if (inspectionsData.length > 0) {
              break; // Found data, stop trying other endpoints
            }
          } catch (endpointError) {
            console.log(`Endpoint ${endpoint} failed:`, endpointError.message);
            continue; // Try next endpoint
          }
        }
        
        if (inspectionsData.length > 0) {
          equipmentData.inspections = inspectionsData;
          equipmentData.latest_inspection = inspectionsData[0]; // Most recent
        }
        
      } catch (inspectionError) {
        console.log('Could not fetch inspections:', inspectionError.message);
        // Continue without inspections data
      }
      
      setEquipment(equipmentData);
    } catch (error) {
      console.error('Error fetching equipment:', error);
      setError('Equipment not found or not accessible');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'issued':
        return 'bg-blue-100 text-blue-800';
      case 'service':
        return 'bg-yellow-100 text-yellow-800';
      case 'reservation':
        return 'bg-purple-100 text-purple-800';
      case 'damaged':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getInspectionStatus = (equipment) => {
    if (!equipment.latest_inspection) {
      return { status: 'no-inspection', color: 'text-gray-500', icon: AlertTriangle };
    }

    const validityDate = new Date(equipment.latest_inspection.validity_date);
    const today = new Date();
    const daysUntilExpiry = Math.floor((validityDate - today) / (1000 * 60 * 60 * 24));

    if (equipment.latest_inspection.status === 'non-ok') {
      return { status: 'failed', color: 'text-red-600', icon: XCircle };
    } else if (daysUntilExpiry < 0) {
      return { status: 'expired', color: 'text-red-600', icon: XCircle };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring', color: 'text-orange-600', icon: AlertTriangle };
    } else {
      return { status: 'valid', color: 'text-green-600', icon: CheckCircle };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Equipment information fields for info tab
  const getEquipmentFields = () => {
    if (!equipment) return [];
    
    return [
      {
        label: 'Equipment Name',
        value: equipment.safety_equipment_type_name || 'N/A',
        icon: Shield,
        color: 'bg-purple-100 text-purple-600'
      },
      {
        label: 'Brand & Model',
        value: `${equipment.safety_equipment_model_brand || equipment.safety_equipment_brand || 'N/A'} ${equipment.safety_equipment_model_name || ''}`.trim(),
        icon: Building,
        color: 'bg-orange-100 text-orange-600'
      },
      {
        label: 'Serial Number',
        value: equipment.safety_equipment_serial_number || 'N/A',
        icon: Package,
        color: 'bg-green-100 text-green-600'
      },
      {
        label: 'Production Date',
        value: equipment.safety_equipment_manufacture_date ? formatDate(equipment.safety_equipment_manufacture_date) : 'N/A',
        icon: Calendar,
        color: 'bg-indigo-100 text-indigo-600'
      },
      {
        label: 'NFC Tag',
        value: equipment.nfc_tag_number || 'No NFC assigned',
        icon: Tag,
        color: 'bg-blue-100 text-blue-600',
        isNFC: true
      }
    ];
  };

  // Handle click outside to close modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Don't render anything if modal is not open
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header with close button */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Safety Equipment Check</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading equipment information...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Equipment Not Found</h3>
              <p className="text-gray-600">{error}</p>
            </div>
          ) : equipment ? (
            <div className="p-4">
              {/* Equipment Image */}
              <div className="bg-gray-50 rounded-lg overflow-hidden mb-4">
                <div className="h-48 bg-white flex items-center justify-center p-4">
                  {(() => {
                    // Priority: equipment picture > model picture (same as mobile app)
                    let imageUrl = null;
                    
                    if (equipment.safety_equipment_picture || equipment.picture) {
                      imageUrl = equipment.safety_equipment_picture || equipment.picture;
                    } else if (equipment.safety_equipment_model?.image || equipment.safety_equipment_model_image) {
                      imageUrl = equipment.safety_equipment_model?.image || equipment.safety_equipment_model_image;
                    }
                    
                    if (!imageUrl) {
                      return (
                        <div className="text-gray-400">
                          <Package className="h-12 w-12 mx-auto mb-2" />
                          <p className="text-sm">No image available</p>
                        </div>
                      );
                    }
                    
                    // If URL doesn't start with http, prepend the base URL (same as mobile app)
                    if (!imageUrl.startsWith('http')) {
                      // Always use ventro.energy for images, regardless of API URL
                      const baseUrl = 'https://ventro.energy';
                      imageUrl = imageUrl.startsWith('/') ? `${baseUrl}${imageUrl}` : `${baseUrl}/${imageUrl}`;
                    }
                    
                    return (
                      <img 
                        src={imageUrl}
                        alt={equipment.safety_equipment_type_name}
                        className="max-h-full max-w-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `
                            <div class="text-gray-400">
                              <svg class="h-12 w-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M20 6h-2.18l-1.21-2.42A1 1 0 0 0 15.72 3H8.28a1 1 0 0 0-.89.58L6.18 6H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2zM12 17a5 5 0 1 1 5-5 5 5 0 0 1-5 5z"/>
                              </svg>
                              <p class="text-sm">Image failed to load</p>
                            </div>
                          `;
                        }}
                      />
                    );
                  })()}
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="border-b border-gray-200 mb-4">
                <nav className="-mb-px flex">
                  {[
                    { id: 'info', label: 'Info', icon: Package },
                    { id: 'specs', label: 'Specification', icon: FileText },
                    { id: 'history', label: 'Inspection History', icon: Clock }
                  ].map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`group inline-flex flex-1 justify-center items-center py-3 px-6 border-b-2 font-medium text-sm ${
                          activeTab === tab.id
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4 mr-2" />
                        {tab.label}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="space-y-4">
                {activeTab === 'info' && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Equipment Information</h3>
                    
                    {/* Equipment Info Table */}
                    <div className="bg-gray-50 rounded-lg overflow-hidden">
                      <table className="min-w-full">
                        <tbody className="divide-y divide-gray-200">
                          {getEquipmentFields().map((field, index) => {
                            const Icon = field.icon;
                            return (
                              <tr key={index} className="hover:bg-gray-100">
                                <td className="px-4 py-3 w-12">
                                  <div className={`p-2 rounded-lg ${field.color}`}>
                                    <Icon className="h-4 w-4" />
                                  </div>
                                </td>
                                <td className="px-4 py-3 font-medium text-gray-900 text-sm whitespace-nowrap">
                                  {field.label}
                                </td>
                                <td className="px-4 py-3 text-gray-700 text-sm">
                                  {field.isNFC ? (
                                    <div className="flex items-center space-x-2">
                                      <span className="font-mono text-sm bg-gray-200 px-2 py-1 rounded">
                                        {field.value}
                                      </span>
                                    </div>
                                  ) : (
                                    field.value
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {activeTab === 'specs' && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Documents & Specifications</h3>
                    
                    {/* Real specification data */}
                    <div className="space-y-3">
                      {(equipment.safety_equipment_model?.specification_file || equipment.safety_equipment_model_specification_file) && (
                        <div className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
                          <div className="flex items-center space-x-3" onClick={() => {
                            const specFile = equipment.safety_equipment_model?.specification_file || equipment.safety_equipment_model_specification_file;
                            let specUrl = specFile;
                            
                            // If URL doesn't start with http, prepend the base URL
                            if (specUrl && !specUrl.startsWith('http')) {
                              const baseUrl = 'https://ventro.energy';
                              specUrl = specUrl.startsWith('/') ? `${baseUrl}${specUrl}` : `${baseUrl}/${specUrl}`;
                            }
                            
                            if (specUrl) {
                              window.open(specUrl, '_blank');
                            }
                          }}>
                            <div className="p-2 bg-red-100 text-red-600 rounded-lg">
                              <FileText className="h-5 w-5" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">Equipment Specification</p>
                              <p className="text-sm text-gray-500">PDF • Technical specifications • Click to open</p>
                            </div>
                            <div className="text-gray-400">
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {equipment.safety_equipment_comment && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                              <FileText className="h-5 w-5" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">Additional Information</p>
                              <p className="text-sm text-gray-500">{equipment.safety_equipment_comment}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {(!equipment.safety_equipment_model?.specification_file && !equipment.safety_equipment_model_specification_file && !equipment.safety_equipment_comment) && (
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500">No specifications available</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'history' && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Inspection History</h3>
                    
                    {/* Full Inspection History */}
                    {equipment.inspections && equipment.inspections.length > 0 ? (
                      <div className="space-y-4">
                        {equipment.inspections.map((inspection, index) => {
                          const isLatest = index === 0;
                          const validityDate = new Date(inspection.validity_date);
                          const today = new Date();
                          const daysUntilExpiry = Math.floor((validityDate - today) / (1000 * 60 * 60 * 24));
                          
                          let statusColor = 'bg-gray-100 text-gray-600';
                          let statusText = 'Unknown';
                          let statusIcon = 'bg-gray-100 text-gray-600';
                          
                          if (inspection.status === 'non-ok') {
                            statusColor = 'bg-red-100 text-red-600';
                            statusIcon = 'bg-red-50 text-red-500';
                            statusText = 'Failed';
                          } else if (daysUntilExpiry < 0) {
                            statusColor = 'bg-red-100 text-red-600';
                            statusIcon = 'bg-red-50 text-red-500';
                            statusText = 'Expired';
                          } else if (daysUntilExpiry <= 30) {
                            statusColor = 'bg-orange-100 text-orange-600';
                            statusIcon = 'bg-orange-50 text-orange-500';
                            statusText = 'Expires Soon';
                          } else {
                            statusColor = 'bg-green-100 text-green-600';
                            statusIcon = 'bg-green-50 text-green-500';
                            statusText = 'Valid';
                          }
                          
                          return (
                            <div key={inspection.id || index} className={`p-4 rounded-lg ${isLatest ? 'bg-blue-50 border-l-4 border-blue-500' : 'bg-gray-50'}`}>
                              <div className="flex items-start space-x-3">
                                <div className={`p-2 rounded-lg flex-shrink-0 ${statusIcon}`}>
                                  {inspection.status === 'non-ok' || daysUntilExpiry < 0 ? 
                                    <XCircle className="h-5 w-5" /> : 
                                    daysUntilExpiry <= 30 ? 
                                    <AlertTriangle className="h-5 w-5" /> : 
                                    <CheckCircle className="h-5 w-5" />
                                  }
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-3">
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <p className="font-medium text-gray-900">
                                          {formatDate(inspection.inspection_date)}
                                        </p>
                                        {isLatest && (
                                          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                                            Latest
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-sm text-gray-500 mt-0.5">
                                        Valid until {formatDate(inspection.validity_date)}
                                      </p>
                                    </div>
                                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${statusColor}`}>
                                      {statusText}
                                    </span>
                                  </div>
                                  
                                  <div className={`grid gap-4 ${inspection.inspection_file ? 'grid-cols-3' : 'grid-cols-2'}`}>
                                    <div>
                                      <p className="text-xs text-gray-500">Inspector</p>
                                      <p className="text-sm font-medium text-gray-900 truncate">{inspection.inspector || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Result</p>
                                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                        inspection.status === 'non-ok' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                      }`}>
                                        {inspection.status === 'non-ok' ? 'Failed' : 'Passed'}
                                      </span>
                                    </div>
                                    {inspection.inspection_file && (
                                      <div>
                                        <p className="text-xs text-gray-500">Inspection File</p>
                                        <button
                                          onClick={() => {
                                            const fileUrl = inspection.inspection_file.startsWith('http') 
                                              ? inspection.inspection_file 
                                              : `https://ventro.energy${inspection.inspection_file.startsWith('/') ? '' : '/'}${inspection.inspection_file}`;
                                            window.open(fileUrl, '_blank');
                                          }}
                                          className="inline-flex items-center space-x-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded hover:bg-blue-100 transition-colors border border-blue-200"
                                        >
                                          <Download className="h-3 w-3" />
                                          <span>Report</span>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {inspection.comments && (
                                    <div className="mt-3">
                                      <p className="text-xs text-gray-500">Comments</p>
                                      <p className="text-sm text-gray-700 line-clamp-2">{inspection.comments}</p>
                                    </div>
                                  )}
                                  
                                  {inspection.inspection_photo && (
                                    <div className="mt-3">
                                      <p className="text-xs text-gray-500 mb-1">Inspection Photo</p>
                                      <img 
                                        src={inspection.inspection_photo.startsWith('http') ? inspection.inspection_photo : `https://ventro.energy${inspection.inspection_photo.startsWith('/') ? '' : '/'}${inspection.inspection_photo}`}
                                        alt="Inspection photo"
                                        className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    </div>
                                  )}
                                  
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : equipment.latest_inspection ? (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <div className={`p-2 rounded-lg flex-shrink-0 ${(() => {
                            const inspectionStatus = getInspectionStatus(equipment);
                            return inspectionStatus.color === 'text-green-600' ? 'bg-green-50 text-green-500' : 
                                   inspectionStatus.color === 'text-red-600' ? 'bg-red-50 text-red-500' : 
                                   inspectionStatus.color === 'text-orange-600' ? 'bg-orange-50 text-orange-500' : 
                                   'bg-gray-100 text-gray-600';
                          })()}`}>
                            {(() => {
                              const inspectionStatus = getInspectionStatus(equipment);
                              const InspectionIcon = inspectionStatus.icon;
                              return <InspectionIcon className="h-5 w-5" />;
                            })()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="mb-3">
                              <p className="font-medium text-gray-900">
                                {formatDate(equipment.latest_inspection.inspection_date)}
                              </p>
                              <p className="text-sm text-gray-500 mt-0.5">
                                Valid until {formatDate(equipment.latest_inspection.validity_date)}
                              </p>
                            </div>
                            
                            <div className={`grid gap-4 ${equipment.latest_inspection.inspection_file ? 'grid-cols-3' : 'grid-cols-2'}`}>
                              <div>
                                <p className="text-xs text-gray-500">Inspector</p>
                                <p className="text-sm font-medium text-gray-900 truncate">{equipment.latest_inspection.inspector || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Result</p>
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                  equipment.latest_inspection.status === 'non-ok' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                }`}>
                                  {equipment.latest_inspection.status === 'non-ok' ? 'Failed' : 'Passed'}
                                </span>
                              </div>
                              {equipment.latest_inspection.inspection_file && (
                                <div>
                                  <p className="text-xs text-gray-500">Inspection File</p>
                                  <button
                                    onClick={() => {
                                      const fileUrl = equipment.latest_inspection.inspection_file.startsWith('http') 
                                        ? equipment.latest_inspection.inspection_file 
                                        : `https://ventro.energy${equipment.latest_inspection.inspection_file.startsWith('/') ? '' : '/'}${equipment.latest_inspection.inspection_file}`;
                                      window.open(fileUrl, '_blank');
                                    }}
                                    className="inline-flex items-center space-x-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded hover:bg-blue-100 transition-colors border border-blue-200"
                                  >
                                    <Download className="h-3 w-3" />
                                    <span>Report</span>
                                  </button>
                                </div>
                              )}
                            </div>
                            
                            {equipment.latest_inspection.comments && (
                              <div className="mt-3">
                                <p className="text-xs text-gray-500">Comments</p>
                                <p className="text-sm text-gray-700 line-clamp-2">{equipment.latest_inspection.comments}</p>
                              </div>
                            )}

                            {equipment.latest_inspection.inspection_photo && (
                              <div className="mt-3">
                                <p className="text-xs text-gray-500 mb-1">Inspection Photo</p>
                                <img 
                                  src={equipment.latest_inspection.inspection_photo.startsWith('http') ? equipment.latest_inspection.inspection_photo : `https://ventro.energy${equipment.latest_inspection.inspection_photo.startsWith('/') ? '' : '/'}${equipment.latest_inspection.inspection_photo}`}
                                  alt="Inspection photo"
                                  className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                            
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-8 rounded-lg text-center">
                        <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500">No inspection history available</p>
                        <p className="text-xs text-gray-400 mt-2">
                          Inspection data may not be publicly accessible
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EquipmentCheckModal;